import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import CustomViewsAPI from '../../api/customViews';

const VIEW_TYPES = {
  CONVERSATION: 'conversation',
  CONTACT: 'contact',
};

// use to normalize the filter type
const FILTER_KEYS = {
  0: VIEW_TYPES.CONVERSATION,
  1: VIEW_TYPES.CONTACT,
  [VIEW_TYPES.CONVERSATION]: VIEW_TYPES.CONVERSATION,
  [VIEW_TYPES.CONTACT]: VIEW_TYPES.CONTACT,
};

// a folder's contact_id filter stores only the id, extract it so the
// contact can be fetched and its name shown in the edit folder modal
const getFolderContactId = folder =>
  folder?.query?.payload?.find(filter => filter.attribute_key === 'contact_id')
    ?.values?.[0];

export const state = {
  [VIEW_TYPES.CONVERSATION]: {
    records: [],
  },
  [VIEW_TYPES.CONTACT]: {
    records: [],
  },
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isDeleting: false,
  },
  activeConversationFolder: null,
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getCustomViewsByFilterType: _state => key => {
    const filterType = FILTER_KEYS[key];
    return _state[filterType].records;
  },
  getConversationCustomViews(_state) {
    return _state[VIEW_TYPES.CONVERSATION].records;
  },
  getContactCustomViews(_state) {
    return _state[VIEW_TYPES.CONTACT].records;
  },
  getActiveConversationFolder(_state) {
    return _state.activeConversationFolder;
  },
  getActiveFolderContactId(_state) {
    return getFolderContactId(_state.activeConversationFolder);
  },
};

const pendingLoads = new WeakMap();

export const actions = {
  get: async function getCustomViews(
    { commit, state: currentState = state },
    filterType = 'conversation'
  ) {
    const type = FILTER_KEYS[filterType];
    const suffix = type === VIEW_TYPES.CONTACT ? 'Contact' : 'Conversation';
    const loadingKey = `isFetching${suffix}`;
    const errorKey = `has${suffix}Error`;
    const requests = pendingLoads.get(currentState) || new Map();
    pendingLoads.set(currentState, requests);
    const request = Symbol(type);
    requests.set(type, request);
    const isCurrent = () => requests.get(type) === request;
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, {
      isFetching: true,
      [loadingKey]: true,
      [errorKey]: false,
    });
    try {
      const response = await CustomViewsAPI.getCustomViewsByFilterType(type);
      if (!isCurrent()) return false;
      commit(types.SET_CUSTOM_VIEW, { data: response.data, filterType: type });
      return true;
    } catch {
      if (isCurrent())
        commit(types.SET_CUSTOM_VIEW_UI_FLAG, { [errorKey]: true });
      return false;
    } finally {
      if (isCurrent()) {
        requests.delete(type);
        commit(types.SET_CUSTOM_VIEW_UI_FLAG, {
          isFetching: requests.size > 0,
          [loadingKey]: false,
        });
      }
    }
  },
  create: async function createCustomViews({ commit }, obj) {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: true });
    try {
      const response = await CustomViewsAPI.create(obj);
      commit(types.ADD_CUSTOM_VIEW, {
        data: response.data,
        filterType: FILTER_KEYS[obj.filter_type],
      });
      return response;
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: false });
    }
  },
  update: async function updateCustomViews({ commit }, obj) {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: true });
    try {
      const response = await CustomViewsAPI.update(obj.id, obj);
      commit(types.UPDATE_CUSTOM_VIEW, {
        data: response.data,
        filterType: FILTER_KEYS[obj.filter_type],
      });
    } catch (error) {
      const errorMessage = error?.response?.data?.message;
      throw new Error(errorMessage);
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isCreating: false });
    }
  },
  delete: async ({ commit }, { id, filterType }) => {
    commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isDeleting: true });
    try {
      try {
        await CustomViewsAPI.deleteCustomViews(id, filterType);
      } catch (error) {
        // 404 (já apagado em outra aba ou no clique repetido) e 5xx confirmam
        // pelo GET, para pasta e para segmento; o resto é erro de verdade
        const status = error?.response?.status;
        if (status && status !== 404 && status < 500) throw error;
        const { data } =
          await CustomViewsAPI.getCustomViewsByFilterType(filterType);
        if (
          !Array.isArray(data) ||
          data.some(view => Number(view.id) === Number(id))
        ) {
          throw error;
        }
      }
      commit(types.DELETE_CUSTOM_VIEW, { data: id, filterType });
    } finally {
      commit(types.SET_CUSTOM_VIEW_UI_FLAG, { isDeleting: false });
    }
  },
  setActiveConversationFolder({ commit, dispatch }, data) {
    commit(types.SET_ACTIVE_CONVERSATION_FOLDER, data);
    // prefetch the contact of a contact filter so the UI can show its name
    const contactId = getFolderContactId(data);
    if (contactId) dispatch('contacts/show', { id: contactId }, { root: true });
  },
};

export const mutations = {
  [types.SET_CUSTOM_VIEW_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.ADD_CUSTOM_VIEW]: (_state, { data, filterType }) => {
    MutationHelpers.create(_state[filterType], data);
  },
  [types.SET_CUSTOM_VIEW]: (_state, { data, filterType }) => {
    MutationHelpers.set(_state[filterType], data);
  },
  [types.UPDATE_CUSTOM_VIEW]: (_state, { data, filterType }) => {
    MutationHelpers.update(_state[filterType], data);
  },
  [types.DELETE_CUSTOM_VIEW]: (_state, { data, filterType }) => {
    MutationHelpers.destroy(_state[filterType], data);
  },

  [types.SET_ACTIVE_CONVERSATION_FOLDER](_state, folder) {
    _state.activeConversationFolder = folder;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
